import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback, useMemo } from 'react';

import { useTextStyle } from '@webapp/common/components/custom-styles';
import { Select } from '@webapp/common/components/select';
import { convertToOptions } from '@webapp/common/components/select/lib';
import { CssUiComponent } from '@webapp/common/components/survey-custom';
import { Textfield } from '@webapp/common/components/textfield';
import { useMobile } from '@webapp/common/hooks/use-mobile';
import { setHtml } from '@webapp/common/lib/ui';
import { AnswerType, DropdownType } from '@webapp/common/resources/survey';

import { usePipingAnswersReplacer } from '@webapp/survey/src/machine/use-piping-answers-replacer';

import type { MatrixCell } from '../matrix-layout/matrix-cell';
import cssLayout from '../matrix-layout/matrix-layout.css';

import css from './matrix-dropdown.css';

export const Group: VFC<MatrixCell> = ({
    answer: { id: answerId },
    group: groupActor,
    height,
    disabled,
    onChange,
    question: {
        params: { answers: questionAnswers, dropdownListType, excludeSelectedAnswer },
    },
}) => {
    const pipingAnswersReplacer = usePipingAnswersReplacer();
    const group = useSelector(groupActor, (g) => g.context);
    const { inputStyle, textStyle } = useTextStyle();
    const isMobile = useMobile();
    const selfStyle = useMemo(() => ({ ...inputStyle, maxWidth: undefined, width: undefined }), [inputStyle]);

    const handleChangeValue = useCallback(
        (value) => {
            if (disabled) {
                onChange?.();
            }

            if (group.type === AnswerType.SELF) {
                groupActor.send({ type: 'CHANGE_RESPONSE_EXTRA', answerId, value });
                groupActor.send({ type: 'CHANGE_RESPONSE_VALUE', answerId, value: true });
            } else {
                groupActor.send({ type: 'CHANGE_RESPONSE_VALUE', answerId, value });
            }
        },
        [answerId, group, disabled],
    );
    const { id, name, type } = group;

    const response = useSelector(group.responses[answerId], (r) => r.context);
    const value = (response?.value || '') as string;
    const extra = (response?.extra || '') as string;
    let answers = dropdownListType === DropdownType.DIFFERENT ? group.answers : questionAnswers;
    const self = type === AnswerType.SELF;

    if (excludeSelectedAnswer) {
        answers = answers.filter((a) => (value && value === a) || !group.responsesValues.includes(a));
    }

    return (
        <div className={cn(CssUiComponent.CELL, cssLayout.cell, css.answer)} key={id} style={{ height }}>
            <div className={css.field}>
                <div
                    className={cn(CssUiComponent.LABEL, css.name)}
                    style={textStyle}
                    {...setHtml(pipingAnswersReplacer(name))}
                />
                <div className={css.text}>
                    {self ? (
                        <Textfield
                            transparent
                            rootClassName={cssLayout.control}
                            style={selfStyle}
                            value={disabled ? '' : extra}
                            onChange={handleChangeValue}
                        />
                    ) : (
                        <Select
                            searchable={true}
                            clearable={true}
                            className={cssLayout.control}
                            options={convertToOptions(answers)}
                            value={disabled ? '' : value}
                            onChange={handleChangeValue}
                            styles={{
                                width: isMobile ? '100%' : 180,
                                maxWidth: isMobile ? undefined : 180,
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
Group.displayName = 'Group';
